/**
 * Recently Viewed Products - Drawer Mode JavaScript
 * Only loaded when drawer mode is enabled
 */

class RecentlyViewedDrawer {
  constructor(config) {
    this.config = config;
    this.trigger = document.querySelector('[data-recently-viewed-trigger]');
    this.recentlyViewedEl = document.querySelector('recently-viewed-products');
    this.drawer = document.querySelector('.recently-viewed-drawer');
    this.scrollContainer = null;
    this.customScrollbar = null;
    this.hideScrollbarTimeout = null;
    this.init();
  }

  init() {
    if (!this.trigger || !this.recentlyViewedEl) {
      return;
    }

    this.checkAndShowTrigger();
    this.setupObserver();
    this.setupCustomScrollbar();
    this.setupCloseButton();
    this.setupDrawerOpenTracking();
    
    if (this.config.autoOpen) {
      this.handleAutoOpen();
    }
  }

  /**
   * Setup tracking for when drawer is manually opened
   */
  setupDrawerOpenTracking() {
    if (!this.trigger || !this.drawer) return;

    this.trigger.addEventListener('click', () => {
      this.markDrawerAsShown();
    });

    if (this.drawer) {
      this.drawer.addEventListener('drawer:open', () => {
        this.markDrawerAsShown();
      });
    }
  }

  /**
   * Check if current page type matches visibility setting
   */
  shouldShowButton() {
    const visibility = this.config.visibility || 'home_collection_product';
    const pageType = this.config.pageType || 'index';
    
    switch (visibility) {
      case 'home':
        return pageType === 'index';
      case 'home_collection':
        return pageType === 'index' || pageType === 'collection';
      case 'home_collection_product':
        return pageType === 'index' || pageType === 'collection' || pageType === 'product';
      default:
        return true;
    }
  }

  /**
   * Check if enough days have passed since drawer was last shown/closed
   */
  hasEnoughDaysPassed() {
    const daysRequired = this.config.daysBeforeShowingAgain || 0;
    
    if (daysRequired === 0) {
      return true;
    }

    try {
      const timeUnit = 1000 * 60 * 60 * 24; // days
      const lastShownTimestamp = localStorage.getItem('recently-viewed-drawer-last-shown');
      
      if (!lastShownTimestamp) {
        return true;
      }

      const lastShown = parseInt(lastShownTimestamp, 10);
      const now = Date.now();
      const timePassed = (now - lastShown) / timeUnit;
      
      return timePassed >= daysRequired;
    } catch (e) {
      console.error('Error checking days passed:', e);
      return true;
    }
  }

  /**
   * Store timestamp when drawer is shown or closed
   */
  markDrawerAsShown() {
    const daysRequired = this.config.daysBeforeShowingAgain || 0;
    
    // Only store timestamp if the feature is enabled
    if (daysRequired > 0) {
      try {
        localStorage.setItem('recently-viewed-drawer-last-shown', Date.now().toString());
      } catch (e) {
        console.error('Error marking drawer as shown:', e);
      }
    }
  }

  /**
   * Check if button has already been shown for this page type in this session
   */
  hasButtonBeenShownForPageType() {
    if (!this.config.showOncePerPage) {
      return false;
    }

    try {
      const pageType = this.config.pageType || 'index';
      const shownPages = JSON.parse(sessionStorage.getItem('recently-viewed-button-shown-pages') || '[]');
      return shownPages.includes(pageType);
    } catch (e) {
      console.error('Error checking shown pages:', e);
      return false;
    }
  }

  /**
   * Mark button as shown for current page type in session
   */
  markButtonAsShownForPageType() {
    if (!this.config.showOncePerPage) {
      return; 
    }

    try {
      const pageType = this.config.pageType || 'index';
      const shownPages = JSON.parse(sessionStorage.getItem('recently-viewed-button-shown-pages') || '[]');
      
      if (!shownPages.includes(pageType)) {
        shownPages.push(pageType);
        sessionStorage.setItem('recently-viewed-button-shown-pages', JSON.stringify(shownPages));
      }
    } catch (e) {
      console.error('Error marking button as shown:', e);
    }
  }

  /**
   * Check if there are products and show trigger button
   */
  checkAndShowTrigger() {
    if (!this.shouldShowButton()) {
      return;
    }

    const daysPassed = this.hasEnoughDaysPassed();
    if (!daysPassed) {
      return;
    }

    if (this.hasButtonBeenShownForPageType()) {
      return;
    }

    try {
      const viewedProducts = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
      
      if (viewedProducts.length > 0) {
        this.trigger.style.display = 'block';
        this.markButtonAsShownForPageType();
      }
    } catch (e) {
      // Silent fail
      console.error('Error checking viewed products:', e);
    }
  }

  /**
   * Setup observer to monitor when products are loaded
   */
  setupObserver() {
    const observer = new MutationObserver((mutations) => {
      if (!this.shouldShowButton()) {
        return;
      }

      if (!this.hasEnoughDaysPassed()) {
        return;
      }

      if (this.hasButtonBeenShownForPageType()) {
        return;
      }

      const hasProducts = this.recentlyViewedEl.querySelector('product-card');
      if (hasProducts) {
        this.trigger.style.display = 'block';
        this.markButtonAsShownForPageType();
      }
    });

    observer.observe(this.recentlyViewedEl, { 
      childList: true, 
      subtree: true 
    });
  }

  /**
   * Setup custom scrollbar
   */
  setupCustomScrollbar() {
    if (!this.drawer) return;

    setTimeout(() => {
      this.scrollContainer = this.drawer.querySelector('.v-stack');
      
      if (!this.scrollContainer) return;

      this.customScrollbar = document.createElement('div');
      this.customScrollbar.className = 'recently-viewed-drawer__scrollbar';
      this.drawer.appendChild(this.customScrollbar);

      // Use  for smoother updates
      let ticking = false;
      this.scrollContainer.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            this.updateScrollbar();
            ticking = false;
          });
          ticking = true;
        }
      });
      
      this.scrollContainer.addEventListener('mouseenter', () => {
        this.customScrollbar.classList.add('recently-viewed-drawer__scrollbar--hover');
      });
      
      this.scrollContainer.addEventListener('mouseleave', () => {
        this.customScrollbar.classList.remove('recently-viewed-drawer__scrollbar--hover');
      });

      this.updateScrollbar();

      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => this.updateScrollbar(), 50);
      });
    }, 50);
  }

  /**
   * Update custom scrollbar position and size
   */
  updateScrollbar() {
    if (!this.scrollContainer || !this.customScrollbar) return;

    const scrollHeight = this.scrollContainer.scrollHeight;
    const clientHeight = this.scrollContainer.clientHeight;
    const scrollTop = this.scrollContainer.scrollTop;

    if (scrollHeight <= clientHeight) {
      this.customScrollbar.classList.remove('recently-viewed-drawer__scrollbar--visible');
      return;
    }

    const scrollbarHeight = (clientHeight / scrollHeight) * clientHeight;
    const scrollbarTop = (scrollTop / scrollHeight) * clientHeight;

    this.customScrollbar.style.height = `${scrollbarHeight}px`;
    this.customScrollbar.style.transform = `translateY(${scrollbarTop + 16}px)`; // 16px = 1rem padding
    this.customScrollbar.style.top = '0';

    this.customScrollbar.classList.add('recently-viewed-drawer__scrollbar--visible');

    clearTimeout(this.hideScrollbarTimeout);
    this.hideScrollbarTimeout = setTimeout(() => {
      this.customScrollbar.classList.remove('recently-viewed-drawer__scrollbar--visible');
    }, 800);
  }

  /**
   * Setup close button to hide the trigger
   */
  setupCloseButton() {
    const closeButton = document.querySelector('[data-recently-viewed-trigger-close]');
    if (!closeButton) return;

    const hideTrigger = (e) => {
      e.stopPropagation();
      this.markDrawerAsShown();
      
      if (this.trigger) {
        this.trigger.style.display = 'none';
        this.triggerWrapper = this.trigger.closest('.recently-viewed-trigger-wrapper');
        if (this.triggerWrapper) {
          this.triggerWrapper.style.display = 'none';
        }
      }
    };

    closeButton.addEventListener('click', hideTrigger);

    closeButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        hideTrigger(e);
      }
    });
  }

  /**
   * Handle auto-opening the drawer on first visit
   */
  handleAutoOpen() {
    const isHomePage = window.location.pathname === '/' || window.location.pathname === '/pages/home';
    const hasAutoOpened = sessionStorage.getItem('recently-viewed-auto-opened');
    
    if (!isHomePage || hasAutoOpened) {
      return;
    }

    if (!this.hasEnoughDaysPassed()) {
      return;
    }

    try {
      const viewedProducts = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
      
      if (viewedProducts.length > 0) {
        setTimeout(() => {
          this.trigger.click();
          sessionStorage.setItem('recently-viewed-auto-opened', 'true');
          this.markDrawerAsShown();
        }, 500);
      }
    } catch (e) {
      console.error('Error auto-opening drawer:', e);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.recentlyViewedDrawerConfig) {
      new RecentlyViewedDrawer(window.recentlyViewedDrawerConfig);
    }
    initHeaderRecentlyViewedTrigger();
  });
} else {
  if (window.recentlyViewedDrawerConfig) {
    new RecentlyViewedDrawer(window.recentlyViewedDrawerConfig);
  }
  initHeaderRecentlyViewedTrigger();
}

/**
 * Header Recently Viewed Products Drawer Trigger Handler
 * Handles the icon in the header that opens the drawer
 */
function initHeaderRecentlyViewedTrigger() {
  const recentlyViewedTrigger = document.querySelector('[data-header-recently-viewed-trigger]');
  const openButton = document.querySelector('[data-open-recently-viewed-drawer]');
  
  if (!recentlyViewedTrigger || !openButton) return;

  function updateVisibility() {
    const hasDrawer = document.querySelector('.recently-viewed-trigger-wrapper, .recently-viewed-drawer');
    if (!hasDrawer) {
      recentlyViewedTrigger.style.display = 'none';
      return;
    }

    try {
      const products = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
      recentlyViewedTrigger.style.display = products.length > 0 ? '' : 'none';
    } catch (e) {
      recentlyViewedTrigger.style.display = 'none';
    }
  }

  function openDrawer() {
    const trigger = document.querySelector('[data-recently-viewed-trigger]');
    if (trigger) {
      trigger.click();
    } else {
      const drawer = document.querySelector('.recently-viewed-drawer');
      if (drawer && typeof drawer.show === 'function') {
        drawer.show();
      }
    }
  }

  openButton.addEventListener('click', (e) => {
    e.preventDefault();
    openDrawer();
  });

  window.addEventListener('storage', (e) => {
    if (e.key === 'theme:recently-viewed-products') updateVisibility();
  });

  updateVisibility();
  setTimeout(updateVisibility, 1000);
  
  const observer = new MutationObserver(() => updateVisibility());
  observer.observe(document.body, { childList: true, subtree: true });
}



